import { useEffect, useRef } from 'react';
import pubSub from '../../../utils/pubsub';

/**
 * Refetches subscription usage when something has spent a balance.
 *
 * One trigger: an agent response completing (busy → idle, TD-2002). Keyed by
 * sessionId so parallel sessions don't swallow each other's transitions
 * (TD-1904 makes agent-state-change session-scoped).
 *
 * There was a second trigger, `subscription-usage-change`, for flows that spend
 * a balance without moving the agent out of idle — a reveal finishing. It is
 * gone: those flows refetch the shared snapshot directly, which reaches this
 * panel, and reacting here as well would only force a second request for the
 * same deduction.
 *
 * @param {Function} refetch - subscription-status refetch
 * @param {boolean} [enabled=true] - false for a controlled consumer whose parent
 *   already owns the fetch, so the same event doesn't fire two requests.
 */
const useAgentUsageRefetch = (refetch, enabled = true) => {
  const prevAgentBusyBySession = useRef(new Map());

  useEffect(() => {
    if (!enabled) return undefined;

    const handleEvent = (payload) => {
      if (payload?.event !== 'agent-state-change') return;
      const { sessionId } = payload;
      if (!sessionId) return;
      const isBusy = payload.state !== 'idle';
      const wasBusy = prevAgentBusyBySession.current.get(sessionId) === true;
      if (wasBusy && !isBusy) {
        refetch();
      }
      prevAgentBusyBySession.current.set(sessionId, isBusy);
    };

    pubSub.subscribe(handleEvent);
    return () => pubSub.unsubscribe(handleEvent);
  }, [refetch, enabled]);
};

export default useAgentUsageRefetch;
